import useSWR from 'swr';

import { useCurrentMonthScope } from '@/hooks/useCurrentMonthScope';
import { homeApi } from '@/lib/api';
import { calendarScopeQuery } from '@/lib/calendarScope';
import type { AvailableToSpend } from '@/types';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from './swrConfig';

/**
 * W1 · Disponível para gastar — safe-to-spend until the next salary from
 * `GET /api/home/available-to-spend` (server-cached per user, 1h TTL). The key
 * rides the registered `home-widget` prefix (FINANCIAL_DATA_KEY_PREFIXES) so
 * mutation/sync revalidation sweeps refresh this widget too.
 *
 * The cycle is the CLIENT's calendar month (`calendarScopeQuery`): the server clock is UTC
 * and would roll the cycle hours before the user's month ends. The key carries the local
 * month so a tab left open across the turn of the month refetches instead of keeping the
 * old cycle.
 */
export function useAvailableToSpend(): {
  data: AvailableToSpend | undefined;
  error: unknown;
  isLoading: boolean;
} {
  const authedFetch = useAuthedFetcher();
  const { year, month } = useCurrentMonthScope();

  const { data, error, isLoading } = useSWR<AvailableToSpend>(
    ['home-widget-available-to-spend', year, month],
    () => authedFetch((token) => homeApi.getAvailableToSpend(token, calendarScopeQuery())),
    LONG_LIVED_SWR_OPTIONS,
  );

  return { data, error, isLoading };
}
